import { faresPublished } from "./flight-search";
import { canPriceFares } from "./trip-dates";
import { MAX_DESTINATIONS, MODEL_HOME_CITIES_LISTED } from "./constants";
import { fromUsd, missingInputs, type MissingInput } from "./estimate";
import type { ReadCheck } from "./chat-messages";
import type { Estimate, HotelRate, RouteFare, UsdRange, Workspace } from "./types";
import { cityAdjustment } from "./cost-index";
import { describeFlight } from "./flight-format";
import { cityKey, effectiveAirlines, unknownTravelers, originKey } from "./workspace";
import { getCurrencySymbol } from "@/features/currency/_logic/iso-4217";

type Rates = Readonly<Record<string, number>>;

/** The airline choice in words, for the model: "only United Airlines (UA), Delta (DL)". */
const describeAirlines = (workspace: Workspace): string => {
  const { mode, codes, names } = effectiveAirlines(workspace.airlines);
  if (mode === "any") return "any (Spirit and Frontier left out)";
  const list = codes.map((code) => (names[code] ? `${names[code]} (${code})` : code)).join(", ");
  return mode === "only" ? `only ${list}` : `not ${list} (Spirit and Frontier also left out)`;
};

/** Rounds a USD amount into the display currency, falling back to USD when the rate is missing. */
const money = (usd: number, workspace: Workspace, rates: Rates): number =>
  Math.round(fromUsd(usd, workspace.currency, rates) ?? usd);

const band = (range: UsdRange, workspace: Workspace, rates: Rates): string => {
  const low = money(range.low, workspace, rates);
  const high = money(range.high, workspace, rates);
  return low === high ? `${low}` : `${low}-${high}`;
};

/** An amount with its currency symbol, for the model to repeat as written: "$143". */
const say = (usd: number, workspace: Workspace, rates: Rates): string =>
  `${getCurrencySymbol(workspace.currency)}${money(usd, workspace, rates).toLocaleString("en-US")}`;

const sayBand = (range: UsdRange, workspace: Workspace, rates: Rates): string => {
  const low = say(range.low, workspace, rates);
  const high = say(range.high, workspace, rates);
  return low === high ? low : `${low}-${high}`;
};

/** How the daily rates apply across cities, for the model. */
const rateBasis = (workspace: Workspace): string => {
  const edited = workspace.assumptions.edited ?? [];
  const named = edited.map((field) => (field === "mealsPerDay" ? "meals and activities" : "meeting space"));
  const flat = named.length
    ? ` The planner set ${named.join(" and ")}, so ${named.length > 1 ? "they apply" : "it applies"} flat to every city.`
    : "";
  return `Default rates are adjusted for each city's costs using public GSA and State Department per diem meal rates. Never mention a cost index or index number to the planner.${flat}`;
};

const MISSING_COPY: Record<MissingInput, string> = {
  dates: "dates (nothing is priced until dates are set: no fares, no hotel totals)",
  origins: "home cities (flights cannot be priced; totals exclude flights)",
  destinations: "destination cities (nothing to compare yet)",
};

/** The flight behind a fare, in words: airline, number, stops, duration, cabin, times, options. */
const flightWords = (route: RouteFare): string => {
  const flight = route.flight;
  if (!flight) return "; flight details not in the search results";
  const lines = describeFlight(flight);
  const parts = [
    lines.title ?? "airline not in the search results",
    lines.route,
    lines.times ? `outbound ${lines.times}` : null,
    flight.otherOptions !== null ? `${flight.otherOptions} other options found` : null,
  ].filter(Boolean);
  return `; ${parts.join(", ")}`;
};

/** One route's fare status in words the model can repeat as fact. */
const routeStatus = (route: RouteFare, workspace: Workspace, rates: Rates): string => {
  const airports = route.airports ? ` (${route.airports.origin}-${route.airports.destination})` : "";
  switch (route.status) {
    case "local":
      return "local, no flight";
    case "needsDates":
      return "needs dates";
    case "unchecked":
      return "not checked (outside the largest home cities)";
    case "loading":
      return "still loading";
    case "override":
      return `planner's own fare ${say(route.usd ?? 0, workspace, rates)} round trip per person`;
    case "priced":
      return `priced ${say(route.usd ?? 0, workspace, rates)} round trip per person${airports}${flightWords(route)}`;
    case "missing":
      // The reason is the status: "Fares aren't published this far ahead yet" is not "no flights".
      return `no fare: ${route.reason ?? "no flights found"}`;
  }
};

/**
 * The workspace's real state as plain text for the chat model: which inputs are missing, each
 * route's fare status, and per-city totals. Facts only, so the model never has to guess why a
 * number is absent. Home cities appear only as city and count, never attendee names.
 * @param workspace - The workspace.
 * @param estimate - Its computed estimate.
 * @param rates - USD-based FX rates.
 * @returns A compact multi-line description in the display currency.
 * @throws Never.
 */
export const describeForModel = (workspace: Workspace, estimate: Estimate, rates: Rates): string => {
  const missing = missingInputs(workspace);
  // A long attendee list is summarized: the largest home cities by name, the rest as one aggregate,
  // so a 100-city estimate stays well inside what the assistant reads.
  const largest = [...workspace.origins].sort((a, b) => b.count - a.count);
  const named = largest.slice(0, MODEL_HOME_CITIES_LISTED);
  const rest = largest.slice(MODEL_HOME_CITIES_LISTED);
  const restPeople = rest.reduce((sum, origin) => sum + origin.count, 0);
  const listed = new Set(named.map(originKey));
  const lines = [
    `Missing inputs: ${missing.length ? missing.map((input) => MISSING_COPY[input]).join("; ") : "none"}`,
    `Title: ${workspace.title}`,
    `Currency: ${workspace.currency} (all amounts below are ${workspace.currency})`,
    `Dates: ${workspace.dates ? `${workspace.dates.start} to ${workspace.dates.end} (${estimate.nights} nights)` : "not set"}${
      workspace.dates && !canPriceFares(workspace.dates)
        ? " (these dates have passed or start in under 3 days, so no fares can be searched: say so first and ask for new dates)"
        : workspace.dates && !faresPublished(workspace.dates)
          ? " (airlines haven't published fares this far ahead, so nothing was searched: say fares open about 11 months before travel; never say no flights were found)"
          : ""
    }`,
    `Cabin: ${workspace.cabin}`,
    `Airlines: ${describeAirlines(workspace)}`,
    `Travelers: ${estimate.headcount}`,
    `Home cities: ${
      workspace.origins.length
        ? named
            .map(
              (origin) =>
                `${origin.group ? `${origin.group} in ` : ""}${origin.city} (${origin.count}${origin.cabin ? `, ${origin.cabin}` : ""})`
            )
            .join(", ") + (rest.length ? `; and ${rest.length} more home cities (${restPeople} people)` : "")
        : "not set"
    }${unknownTravelers(workspace) > 0 ? `; ${unknownTravelers(workspace)} more without a home city (their flights aren't priced)` : ""}`,
    `Assumptions per person: meeting space ${sayBand(workspace.assumptions.meetingSpacePerDay, workspace, rates)}/day, meals and activities ${sayBand(workspace.assumptions.mealsPerDay, workspace, rates)}/day (the planner edits these on the comparison's rows). ${rateBasis(workspace)}`,
    `Destinations (${workspace.destinations.length} of ${MAX_DESTINATIONS}): ${workspace.destinations.length ? workspace.destinations.join(", ") : "none yet"}`,
  ];
  if (workspace.origins.length && workspace.destinations.length) {
    lines.push("Fare status per route:");
    for (const city of estimate.cities) {
      for (const route of city.flights.routes.filter((entry) => listed.has(entry.key))) {
        const who = route.group ? `${route.group} from ${route.origin}` : route.origin;
        lines.push(`- ${who} to ${city.city} (${route.count}): ${routeStatus(route, workspace, rates)}`);
      }
      const others = city.flights.routes.filter((entry) => !listed.has(entry.key));
      if (others.length) {
        const fares = others.flatMap((entry) => (entry.usd !== null ? [entry.usd] : []));
        const people = others.reduce((sum, entry) => sum + entry.count, 0);
        const range = fares.length
          ? `fares ${say(Math.min(...fares), workspace, rates)} to ${say(Math.max(...fares), workspace, rates)}`
          : "no fares yet";
        lines.push(
          `- ${others.length} other home cities to ${city.city} (${people} people): ${fares.length} priced, ${others.length - fares.length} without a fare; ${range}`
        );
      }
    }
  }
  if (workspace.dates) {
    lines.push(
      estimate.withoutHomeCity > 0
        ? `City totals (they exclude flights for the ${estimate.withoutHomeCity} people without a home city; say so whenever you quote a total):`
        : "City totals:"
    );
    for (const city of estimate.cities) {
      const flights = city.flights.loading
        ? "loading"
        : city.flights.averageUsd === null
          ? "none priced"
          : `${say(city.flights.averageUsd, workspace, rates)} avg`;
      const hotel =
        city.hotel.source === "proposals"
          ? `${sayBand(city.hotel.total, workspace, rates)} (from your proposals, ${city.hotel.venueCount} quoting venues)`
          : city.hotel.source === "venues"
            ? `${sayBand(city.hotel.total, workspace, rates)} (${city.hotel.venueCount} venues)`
            : "no rate data, left out";
      const quoted = [
        city.sources.meeting === "proposals"
          ? `meeting space ${sayBand(city.meetingSpace, workspace, rates)} pp`
          : null,
        city.sources.meals === "proposals" ? `food and beverage ${sayBand(city.meals, workspace, rates)} pp` : null,
      ].filter(Boolean);
      const fromProposals = quoted.length ? `; from your proposals: ${quoted.join(", ")}` : "";
      const adjusted = cityAdjustment(city);
      const daily = `; meeting space ${sayBand(city.meetingSpace, workspace, rates)} pp, meals and activities ${sayBand(city.meals, workspace, rates)} pp${adjusted ? ` (${adjusted})` : ""}`;
      const left = [
        city.flights.averageUsd === null ? "flights" : null,
        city.hotel.source === "none" && !city.hotel.loading ? "hotel" : null,
      ].filter(Boolean);
      const excludes = left.length ? ` (excludes ${left.join(" and ")})` : "";
      lines.push(
        `- ${city.city}: flights ${flights}; hotel ${hotel}${fromProposals}${daily}; per person ${sayBand(city.perPerson, workspace, rates)}${excludes}; total ${sayBand(city.total, workspace, rates)}`
      );
    }
    if (estimate.lowestCity) lines.push(`Lowest per person: ${estimate.lowestCity}`);
  }
  return lines.join("\n");
};

/**
 * The estimate as a readable note body for an event's Notes tab.
 * @param workspace - The workspace.
 * @param estimate - Its computed estimate.
 * @param formatRange - Formats a USD range in the display currency.
 * @returns Plain text, one line per city plus the inputs behind it.
 * @throws Never.
 */
export const estimateToNote = (
  workspace: Workspace,
  estimate: Estimate,
  formatRange: (range: UsdRange, compact?: boolean) => string
): string => {
  const lines = [
    `${estimate.headcount} people${workspace.dates ? `, ${workspace.dates.start} to ${workspace.dates.end} (${estimate.nights} nights)` : ""}, ${workspace.cabin}.`,
    workspace.origins.length
      ? `From ${workspace.origins.map((origin) => `${origin.city} (${origin.count})`).join(", ")}.`
      : "",
    "",
    ...estimate.cities.map((city) => {
      const missing = city.flights.missing.length
        ? ` No fare from ${city.flights.missing.map((gap) => `${gap.city} (${gap.count})`).join(", ")}, left out of the average.`
        : "";
      const noHotel = city.hotel.source === "none" ? " No hotel rate data, so the hotel is left out." : "";
      const adjustment = cityAdjustment(city);
      const adjusted = adjustment ? ` ${adjustment.charAt(0).toUpperCase()}${adjustment.slice(1)}.` : "";
      return `${city.city}${city.city === estimate.lowestCity ? " (lowest)" : ""}: ${formatRange(city.perPerson)} per person, ${formatRange(city.total, true)} total.${missing}${noHotel}${adjusted}`;
    }),
    "",
    `Includes flights (live fares), hotel (venue rates), meeting space at ${formatRange(workspace.assumptions.meetingSpacePerDay)} and meals and activities at ${formatRange(workspace.assumptions.mealsPerDay)} per person a day${estimate.cities.some((city) => cityAdjustment(city) !== null) ? ", adjusted for each city's costs from GSA and State Department per diem meal rates" : ""}.`,
  ];
  return lines
    .filter((line, index) => line !== "" || lines[index - 1] !== "")
    .join("\n")
    .trim();
};

/**
 * What a read of the estimate actually found, for an honest caption.
 * @param workspace - The workspace.
 * @param estimate - Its computed estimate.
 * @param hotels - Hotel rates keyed by city.
 * @returns Whether dates are set, routes priced and missing, and cities with hotel data.
 * @throws Never.
 */
export const summarizeCheck = (
  workspace: Workspace,
  estimate: Estimate,
  hotels: ReadonlyMap<string, HotelRate>
): ReadCheck => {
  const routes = estimate.cities.flatMap((city) => city.flights.routes);
  const hotelCities = workspace.destinations.filter((city) => hotels.get(cityKey(city))?.status === "venues").length;
  return {
    hasDates: workspace.dates !== null,
    faresPriced: routes.filter((route) => route.status === "priced").length,
    faresMissing: routes.filter((route) => route.status === "missing").length,
    hotelCities,
  };
};
